import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

import { GALLERY, img } from "@/lib/images";
import { IMAGES } from "@/lib/images";
import { JsonLd } from "@/components/site/JsonLd";
import { localBusinessSchema, breadcrumbSchema } from "@/lib/schema";
import { Reveal } from "@/components/site/Reveal";
import { SectionHeading } from "@/components/site/SectionHeading";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { InnerHero } from "@/components/site/PageBits";
import { CtaBand } from "@/components/site/CTA";

export const Route = createFileRoute("/gallery")({
  head: () => ({
    meta: [
      { title: "Gallery | Pretty in Pink Cleaning Service" },
      {
        name: "description",
        content:
          "Photos of freshly cleaned homes by Pretty in Pink Cleaning Service in Wetumpka, AL and surrounding areas — kitchens, bathrooms, floors, and more.",
      },
      { property: "og:title", content: "Gallery | Pretty in Pink Cleaning Service" },
      {
        property: "og:description",
        content:
          "Photos of freshly cleaned homes by Pretty in Pink Cleaning Service in Wetumpka, AL.",
      },
      { property: "og:type", content: "website" },
      { property: "og:image", content: IMAGES.coverBanner },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: IMAGES.coverBanner },
    ],
  }),
  component: GalleryPage,
});

function GalleryPage() {
  const [index, setIndex] = useState<number | null>(null);

  const close = () => setIndex(null);
  const prev = () => setIndex((i) => (i === null ? i : (i - 1 + GALLERY.length) % GALLERY.length));
  const next = () => setIndex((i) => (i === null ? i : (i + 1) % GALLERY.length));

  return (
    <>
      <JsonLd data={localBusinessSchema()} />
      <JsonLd
        data={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Gallery", path: "/gallery" },
        ])}
      />
      <InnerHero
        eyebrow="Gallery"
        title="Pretty-clean spaces"
        subtitle="A look at the kind of results we aim for on every visit. Tap any photo to view it larger."
      >
        <div className="mb-4">
          <Breadcrumbs
            items={[
              { name: "Home", path: "/" },
              { name: "Gallery", path: "/gallery" },
            ]}
          />
        </div>
      </InnerHero>

      <section className="px-4 py-12 md:px-6 md:py-16">
        <Reveal>
          <SectionHeading
            title="Our work"
            intro="Freshly cleaned kitchens, bathrooms, floors, and more from homes across the River Region."
          />
        </Reveal>
        <div className="mx-auto mt-10 grid max-w-6xl grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
          {GALLERY.map((g, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIndex(i)}
              className="lift-card group block overflow-hidden rounded-2xl border border-border bg-card"
              aria-label={`View photo: ${g.alt}`}
            >
              <img
                src={img(g.src, 640)}
                srcSet={`${img(g.src, 320)} 320w, ${img(g.src, 480)} 480w, ${img(g.src, 640)} 640w, ${img(g.src, 800)} 800w`}
                sizes="(min-width: 1024px) 380px, (min-width: 768px) 33vw, 50vw"
                alt={g.alt}
                loading="lazy"
                decoding="async"
                width={1200}
                height={1500}
                className="aspect-[4/5] w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </button>
          ))}
        </div>
      </section>

      {/* Lightbox — opens the 1200px version */}
      {index !== null && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/80 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label="Photo viewer"
          onClick={close}
        >
          <button
            type="button"
            onClick={close}
            className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            className="absolute left-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 md:left-6"
            aria-label="Previous"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            className="absolute right-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 md:right-6"
            aria-label="Next"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
          <figure className="max-h-[85vh] max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <img
              src={img(GALLERY[index]!.src, 1200)}
              alt={GALLERY[index]!.alt}
              className="max-h-[78vh] w-auto rounded-xl object-contain"
            />
            <figcaption className="mt-3 text-center text-sm text-white/90">
              {GALLERY[index]!.alt}
            </figcaption>
          </figure>
        </div>
      )}

      <CtaBand
        title="Want your space to look this clean?"
        subtitle="Send us a message or give us a call and we'll put together a quote for your home or business."
      />
    </>
  );
}
